// AI Ghostwriter Pro - Contact Page
import React, { useState } from 'react';
import { SeoHead } from '../components/SeoHead';
import { Button } from '../components/ui/button';
import { CheckCircle, Send, Mail, MapPin, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function GhostwriterContact() {
  const [form, setForm] = useState({ name: '', email: '', message: '', genre_interest: '' });
  const [status, setStatus] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const resp = await fetch(`${API_URL}/api/ghostwriter/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (resp.ok) {
        setStatus('sent');
        setForm({ name: '', email: '', message: '', genre_interest: '' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SeoHead
        title="Contact | AI Ghostwriter Pro - Get in Touch"
        description="Contact AI Ghostwriter Pro for questions about our AI book writing service. We respond within 24 hours. Based in Arlington, Texas."
        canonical="https://aighostwriterpro.com/contact"
        og={{ type: 'website', url: 'https://aighostwriterpro.com/contact', title: 'Contact AI Ghostwriter Pro', description: 'Get in touch about our AI book writing service.', siteName: 'AI Ghostwriter Pro' }}
        twitter={{ card: 'summary', title: 'Contact AI Ghostwriter Pro', description: 'Get in touch.' }}
      />

      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4" data-testid="contact-heading">
            Get in Touch
          </h1>
          <p className="text-lg text-gray-300 max-w-xl mx-auto">
            Have a question or ready to start your book? We'd love to hear from you.
          </p>
        </div>

        <div className="max-w-4xl mx-auto grid md:grid-cols-5 gap-8">
          {/* Contact Info */}
          <div className="md:col-span-2 space-y-6">
            <div className="flex items-start gap-3">
              <Mail className="w-5 h-5 text-purple-400 mt-1" />
              <div>
                <p className="text-white font-medium">Email</p>
                <p className="text-gray-400 text-sm">no_reply@aighostwriterpro.com</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-purple-400 mt-1" />
              <div>
                <p className="text-white font-medium">Location</p>
                <p className="text-gray-400 text-sm">Arlington, Texas, USA</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-purple-400 mt-1" />
              <div>
                <p className="text-white font-medium">Response Time</p>
                <p className="text-gray-400 text-sm">Within 24 hours</p>
              </div>
            </div>
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-5 mt-8">
              <p className="text-white font-medium mb-2">AI Ghostwriter Pro</p>
              <p className="text-gray-400 text-sm">A Web-Tech Services product</p>
              <p className="text-gray-500 text-xs mt-2">Part of the <a href="https://upost.com" className="text-purple-400 hover:underline">UPost</a> platform</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-3 bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
            {status === 'sent' ? (
              <div className="text-center py-12" data-testid="contact-success">
                <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-white mb-2">Message Sent!</h3>
                <p className="text-gray-400">We'll get back to you within 24 hours. Check your email for a confirmation.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Name</label>
                  <input type="text" required className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="Your name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Email</label>
                  <input type="email" required className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 focus:border-transparent" placeholder="your@email.com" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="contact-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Genre Interest</label>
                  <select className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-purple-500" value={form.genre_interest} onChange={e => setForm(f => ({ ...f, genre_interest: e.target.value }))} data-testid="contact-genre-input">
                    <option value="">Select a genre (optional)</option>
                    <option value="romance">Romance</option>
                    <option value="mystery">Mystery / Thriller</option>
                    <option value="scifi">Science Fiction</option>
                    <option value="fantasy">Fantasy</option>
                    <option value="selfhelp">Self-Help</option>
                    <option value="business">Business</option>
                    <option value="biography">Biography / Memoir</option>
                    <option value="howto">How-To / Guide</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Your Message</label>
                  <textarea required rows={5} className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:ring-2 focus:ring-purple-500 resize-none" placeholder="Tell us about your book idea, questions, or how we can help..." value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} data-testid="contact-message-input" />
                </div>
                <Button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700" disabled={status === 'sending'} data-testid="contact-submit-btn">
                  {status === 'sending' ? 'Sending...' : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                </Button>
                {status === 'error' && <p className="text-red-400 text-sm text-center">Something went wrong. Please try again.</p>}
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default GhostwriterContact;
